import { Assignment, MarkEmailRead } from '@mui/icons-material'
import {
  Button,
  Chip,
  CircularProgress,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Card,
} from '@mui/material'
import { List } from '@refinedev/mui'
import { format } from 'date-fns'
import { Link } from 'react-router-dom'

import { useScoutAssignmentsQuery } from '../../api'
import type { Appointment } from '../../types.ts'
import { getAssignmentStatusColor } from '../config.ts'
import Header from '../Header'

export type Props = {
  appointment: Appointment | undefined
}

export const ScoutAssignments = ({ appointment }: Props) => {
  const { data: assignmentsData, isLoading: assignmentsLoading } = useScoutAssignmentsQuery(
    appointment?.uuid || '',
    { enabled: !!appointment?.uuid }
  )

  return (
    <Box sx={{ flex: '1 1 100%' }}>
      <Header label="Scout Assignments" />
      {(() => {
        if (assignmentsLoading) {
          return (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Loading assignments...
              </Typography>
            </Box>
          )
        }
        if (assignmentsData?.data && assignmentsData.data.length > 0) {
          return (
            <List headerProps={{ sx: { display: 'none' } }}>
              {assignmentsData.data.map(assignment => (
                <ListItem key={assignment.UUID} sx={{ py: 1 }}>
                  <ListItemIcon>
                    <Assignment color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        flexWrap="wrap"
                        gap={1}
                      >
                        <Typography
                          variant="body2"
                          component={Link}
                          to={`/scouts/show/${assignment.ScoutUUID}`}
                          sx={{
                            textDecoration: 'none',
                            color: 'primary.main',
                            '&:hover': {
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          {assignment.ScoutFirstName} {assignment.ScoutLastName}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          {/* Scheduling Options */}
                          <Box display="flex" gap={0.5}>
                            {[1, 2, 3].map(option => {
                              const proposedDate =
                                appointment &&
                                (appointment[
                                  `proposedDate${option}` as keyof typeof appointment
                                ] as string | null)
                              const formattedDate = proposedDate
                                ? format(new Date(proposedDate), 'dd.MM.yyyy')
                                : null
                              return (
                                <Button
                                  key={option}
                                  size="small"
                                  variant="outlined"
                                  disabled={!proposedDate}
                                  component={Link}
                                  to={`/scheduling/scout?appointmentUuid=${appointment?.uuid}&scoutUuid=${assignment.ScoutUUID}&option=${option}`}
                                  sx={{
                                    minWidth: 'auto',
                                    px: 0.5,
                                    py: 0.25,
                                    fontSize: '0.7rem',
                                    minHeight: '24px',
                                  }}
                                >
                                  {formattedDate || option}
                                </Button>
                              )
                            })}
                          </Box>
                          <Chip
                            label={assignment.Status}
                            size="small"
                            color={getAssignmentStatusColor(assignment.Status)}
                          />
                          {assignment.Distance && (
                            <Typography variant="caption" color="text.secondary">
                              {(assignment.Distance / 1000).toFixed(1)} km
                            </Typography>
                          )}
                          {assignment.EmailStatus === 'sent' && (
                            <MarkEmailRead
                              fontSize="small"
                              color="success"
                              titleAccess="Email sent"
                            />
                          )}
                        </Box>
                      </Box>
                    }
                    secondary={
                      <Box>
                        {assignment.SelectedStartTime && (
                          <Typography variant="caption" color="text.secondary">
                            Selected Time:{' '}
                            {format(new Date(assignment.SelectedStartTime), 'PP HH:mm')}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" display="block">
                          Scout ID: {assignment.ScoutUUID}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )
        }
        return (
          <Typography variant="body2" color="text.secondary">
            No scout assignments found
          </Typography>
        )
      })()}
    </Box>
  )
}

export default Card
